import { AddCard, DeleteOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { getCompanies } from '../../../../http/CompaniesApi';
import {
  addTransaction as callAddTransaction,
  deleteTransaction as callDeleteTransaction,
  getTransactions,
} from '../../../../http/PortfoliosApi';
import AddTransactionDialog from './dialogs/AddTransactionDialog';
import DeleteTransactionDialog from './dialogs/DeleteTransactionDialog';

const PAGE_SIZE = 10;

export default function TransactionsListComponent({
  portfolio,
  transactions,
  reloadTransactions,
  loadPortfolio,
  errMessage,
  setErrMessage,
  setSnackOpen,
  page,
  setPage,
  onEditCash,
}) {
  const [addTransactionDialogOpen, setAddTransactionDialogOpen] =
    React.useState(false);
  const [companies, setCompanies] = useState([]);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterType, setFilterType] = useState('');
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  useEffect(() => {
    async function getData() {
      const { data } = await getCompanies();
      setCompanies(data);
    }

    getData();
  }, []);

  const loadPage = useCallback(
    (newPage) =>
      reloadTransactions(newPage * PAGE_SIZE, PAGE_SIZE, {
        companyId: filterCompanyId,
        type: filterType,
      }),
    [filterCompanyId, filterType, reloadTransactions],
  );

  useEffect(() => {
    // Reset to page 0 when filtering to avoid "empty" pages
    setPage(0);
    loadPage(0);
  }, [loadPage, setPage]);

  const handleChangePage = (event, newPage) => {
    loadPage(newPage);
    setPage(newPage);
  };

  // loadPortfolio also resets the table to an unfiltered first page, so the
  // filtered page has to be reloaded after it.
  const reloadAfterChange = async (targetPage) => {
    await loadPortfolio();
    setPage(targetPage);
    await loadPage(targetPage);
  };

  const handleDeleteTransaction = async (transaction) => {
    setTransactionToDelete(null);
    setDeletingId(transaction.id);

    try {
      await callDeleteTransaction(portfolio?.id, transaction.id);
    } catch (err) {
      setErrMessage(get(err, 'response.data.data.message', errMessage));
      setSnackOpen(true);
      setDeletingId(null);
      return;
    }

    try {
      const { data: remaining } = await getTransactions(portfolio?.id, 0, 1, {
        companyId: transaction.companyId,
      });
      // Reload the whole page, not just drop the row: deleting an old buy
      // changes the PnL of later sells. Step back if the page is now empty.
      const onlyRowOnPage = transactions?.items?.length === 1;
      await reloadAfterChange(onlyRowOnPage && page > 0 ? page - 1 : page);
      setDeleteResult({
        companyName: transaction.companyName,
        positionClosed: remaining?.count === 0,
      });
    } catch (err) {
      setErrMessage(
        'Transaction deleted, but refreshing the portfolio failed. Reload the page.',
      );
      setSnackOpen(true);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (date) => (date ? format(new Date(date), 'dd/MM/yy') : '');

  const formatAmount = (amount, currency) => {
    if (!amount || !currency) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getTypeColor = (type) =>
    type?.toLowerCase() === 'buy'
      ? 'success'
      : type?.toLowerCase() === 'sell'
      ? 'error'
      : 'default';

  const getPnLColor = (pnlAmount, type) => {
    if (type === 'buy') {
      return Number(pnlAmount) > 0 ? 'green' : 'error';
    }
    return 'black';
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Asset</InputLabel>
          <Select
            value={filterCompanyId}
            label="Asset"
            onChange={(e) => setFilterCompanyId(e.target.value)}
          >
            <MenuItem value="">
              <em>All Assets</em>
            </MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filterType}
            label="Type"
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="">
              <em>All Types</em>
            </MenuItem>
            <MenuItem value="buy">Buy</MenuItem>
            <MenuItem value="sell">Sell</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          onClick={() => {
            setFilterCompanyId('');
            setFilterType('');
          }}
          sx={{ textTransform: 'none' }}
        >
          Reset Filters
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Asset</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Shares
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Price
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Volume
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                PnL Amount
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                PnL Percentage
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions?.items?.length > 0 ? (
              transactions.items.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>{formatDate(transaction.timestamp)}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {transaction.companyName}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.type}
                      color={getTypeColor(transaction.type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{transaction.shares}</TableCell>
                  <TableCell align="right">
                    {formatAmount(transaction.price, transaction.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatAmount(transaction.volume, transaction.currency)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      label={transaction.pnlAmount}
                      color={getPnLColor(
                        transaction.pnlAmount,
                        transaction.type,
                      )}
                      fontSize="small"
                    >
                      {formatAmount(
                        transaction.pnlAmount,
                        transaction.currency,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      label={transaction.pnlPercentage}
                      color={getPnLColor(
                        transaction.pnlPercentage,
                        transaction.type,
                      )}
                      fontSize="small"
                      fontWeight="bold"
                    >
                      {transaction.pnlPercentage}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {deletingId === transaction.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Tooltip title="Delete transaction">
                        <span>
                          <IconButton
                            size="small"
                            disabled={deletingId !== null}
                            onClick={() => setTransactionToDelete(transaction)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ py: 3, color: 'text.secondary' }}
                >
                  No transactions recorded.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={transactions?.count || 0}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
        page={page}
        onPageChange={handleChangePage}
        sx={{ borderTop: '1px solid divider' }}
      />
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddCard />}
          onClick={() => setAddTransactionDialogOpen(true)}
        >
          Add transaction
        </Button>
      </Box>
      <AddTransactionDialog
        addTransaction={async (data) => {
          await callAddTransaction(portfolio?.id, data);
          await reloadAfterChange(0);
        }}
        addTransactionDialogOpen={addTransactionDialogOpen}
        setAddTransactionDialogOpen={setAddTransactionDialogOpen}
        errMessage={errMessage}
        portfolio={portfolio}
        setErrMessage={setErrMessage}
        setSnackOpen={setSnackOpen}
        companies={companies}
      />
      <DeleteTransactionDialog
        transaction={transactionToDelete}
        formatAmount={formatAmount}
        formatDate={formatDate}
        onCancel={() => setTransactionToDelete(null)}
        onConfirm={handleDeleteTransaction}
      />
      <Snackbar
        open={!!deleteResult}
        autoHideDuration={10000}
        onClose={() => setDeleteResult(null)}
      >
        <Alert
          severity="success"
          onClose={() => setDeleteResult(null)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setDeleteResult(null);
                onEditCash();
              }}
            >
              Edit cash
            </Button>
          }
        >
          Transaction deleted. Cash was not adjusted: update it if needed.
          {deleteResult?.positionClosed &&
            ` ${deleteResult.companyName} has no transactions left, so its position was removed: rebalance the target weights.`}
        </Alert>
      </Snackbar>
    </Box>
  );
}
